<template>
  <div>
    <div :class="{ 'has-error': hourlyRateBeneathLimit }" class="form-group text-center">
      <div :class="{ 'col-md-4 control-label': inlineForm }" class="">
        <label>{{ $t('What is your desired pay rate') }}?</label>
      </div>

      <div :class="{ 'col-md-6 text-left': inlineForm }">
        <div v-if="showDailyRateOption" class="m-b-25 m-t-5">
          {{ $t('Daily') }}
          <div
            class="toggle-switch toggle-switch-demo toggle-switch-keep-color m-l-10 m-r-10"
            data-ts-color="black"
          >
            <input
              id="ts3"
              :checked="rateType === 'hourly'"
              type="checkbox"
              hidden="hidden"
              @change="toggleRateType"
            />
            <label for="ts3" class="ts-helper" />
          </div>
          {{ $t('Hourly') }}
        </div>

        <div class="m-b-15">
          <div
            :class="{
              'col-sm-6 col-sm-offset-3 col-xs-10 col-xs-offset-1': !inlineForm,
            }"
            class="input-group"
          >
            <div v-show="!dailyRateSelected">
              <InputCurrency
                v-model="hourlyRateObj"
                data-test-prefix="hourly"
                @update:model-value="calcDailyRate"
              >
                <template #input-label>
                  {{ $t('Hourly') }}
                  <small>({{ currency.toUpperCase() }})</small>
                </template>
              </InputCurrency>
            </div>
            <div v-show="dailyRateSelected">
              <InputCurrency
                v-model="dailyRateObj"
                data-test-prefix="daily"
                @update:model-value="calcHourlyRate"
              >
                <template #input-label>
                  {{ $t('Daily') }}
                  <small>({{ currency.toUpperCase() }})</small>
                </template>
              </InputCurrency>
            </div>
          </div>
          <input :value="hourlyRate" type="hidden" name="hour_rate" />
          <input v-if="showDailyRateOption" :value="dailyRate" type="hidden" name="daily_rate" />
          <div
            v-if="showDailyRateOption"
            :class="{ 'text-center': !inlineForm }"
            class="f-13 m-t-5 c-gray"
          >
            {{
              dailyRateSelected
                ? $t('Estimated hourly equivalent is')
                : $t('Estimated daily equivalent is')
            }}
            {{ dailyRateSelected ? hourlyRate.toFixed(2) : dailyRate.toFixed(2) }}
          </div>
        </div>
        <div v-show="hourlyRateBeneathLimit" id="hour_rate-error" class="help-block m-b-20">
          {{ minimumHourlyRateErrorText }}
        </div>

        <div :class="{ 'row text-center': !inlineForm }" class="">
          <a
            v-show="!shouldDisplayMonthlyInput"
            id="linkToOpenMonthlyInput"
            :title="
              $t('If you get a project where you are at the company full time for 1 month or more.')
            "
            href="#"
            data-toggle="tooltip"
            @click="showMonthlyInput"
          >
            <br />
            {{ $t('Also add monthly rate') }}
            <span class="c-indigo-500"><i class="zmdi zmdi-help" /></span>
          </a>
        </div>
        <div
          v-show="shouldDisplayMonthlyInput"
          id="monthly_rate_wrap"
          :class="{ 'form-group text-center': !inlineForm }"
          class=""
        >
          <label for="monthly_rate">
            {{ $t('Your desired monthly rate') }}
          </label>
          <div
            :class="{
              'col-sm-4 col-sm-offset-4 col-xs-6 col-xs-offset-3': !inlineForm,
            }"
            class="input-group"
          >
            <span class="input-group-addon">{{ currency.toUpperCase() }}</span>
            <input
              id="monthly-rate"
              v-model.number="monthlyRate"
              class="form-control numbersonly"
              placeholder="Enter amount"
              name="monthly_rate"
              type="number"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InputCurrency from '@/components/InputCurrency.vue'

/**
 * NOTE: This is currently used in an html form, and to persist the data the form is submitted
 * thus, there is an initial hourly & monthly rate here which is passed in as a prop from PHP.
 * This will need to be updated to use parent or vuex state once we have higher vue adoption
 * and an api for persistence.
 */
export default {
  name: 'InputPayRates',
  components: { InputCurrency },
  props: {
    market: { type: String, required: true },
    marketPreferredRateType: { type: String, default: 'hourly' },
    currency: { type: String, required: true },
    minimumHourlyRate: { type: Number, required: true },
    minimumHourlyRateErrorText: { type: String, required: true },
    commissionPercentage: { type: Number, required: true },
    persistentHourlyRate: { type: Number, required: true },
    persistentDailyRate: { type: Number, default: 0 },
    persistentMonthlyRate: { type: Number, required: true },
    forceShowMonthlyRateInput: { type: Boolean, default: false },
    inlineForm: { type: Boolean, default: false },
  },
  data() {
    return {
      hourlyRateObj: { integer: 100, fraction: 55 },
      dailyRateObj: { integer: 300, fraction: 22 },
      rateTypesAvailable: ['hourly', 'daily'],
      selectedRateType: '',
      monthlyRate: 0,
      monthlyRateEnabled: false,
    }
  },
  computed: {
    hourlyRate() {
      return parseFloat(`${this.hourlyRateObj.integer}.${this.hourlyRateObj.fraction}`)
    },
    dailyRate() {
      return parseFloat(`${this.dailyRateObj.integer}.${this.dailyRateObj.fraction}`)
    },
    rateType() {
      if (this.selectedRateType) {
        return this.selectedRateType
      }
      if (parseFloat(this.persistentDailyRate) > 0 && this.marketPreferredRateType === 'daily') {
        return 'daily'
      }
      if (parseFloat(this.persistentHourlyRate) > 0) {
        return 'hourly'
      }

      return this.marketPreferredRateType
    },
    dailyRateSelected() {
      return this.rateType === 'daily'
    },
    hourlyRateSelected() {
      return this.rateType === 'hourly'
    },
    shouldDisplayMonthlyInput() {
      // if they have clicked the link or if the component already had an initial monthly value
      return parseFloat(this.persistentMonthlyRate) > 0 || this.monthlyRateEnabled
    },
    showDailyRateOption() {
      return this.marketPreferredRateType === 'daily'
    },
    hourlyRateBeneathLimit() {
      return this.hourlyRate > 0 && this.hourlyRate < this.minimumHourlyRate
    },
  },
  watch: {
    hourlyRate(newRate) {
      this.$emit('rate-changed', 'hourly', newRate)
    },
    dailyRate(newRate) {
      this.$emit('rate-changed', 'daily', newRate)
    },
    monthlyRate(newRate) {
      this.$emit('rate-changed', 'monthly', newRate)
    },
    hourlyRateBeneathLimit(isBeneathLimit) {
      this.$emit('block-toggled', isBeneathLimit)
    },
  },
  created() {
    // If using daily rates, use the daily_rate prop to calc hourly
    // otherwise use hourly rate to calc the daily rate
    if (this.marketPreferredRateType === 'daily' && this.persistentDailyRate) {
      this.dailyRate = this.persistentDailyRate
      const dailyRateString = this.persistentDailyRate.toFixed(2)
      this.dailyRateObj = {
        integer: dailyRateString.slice(0, -3),
        fraction: dailyRateString.slice(-2),
      }
      this.calcHourlyRate()
    } else {
      const hourlyRateString = this.persistentHourlyRate.toFixed(2)
      this.hourlyRateObj = {
        integer: hourlyRateString.slice(0, -3),
        fraction: hourlyRateString.slice(-2),
      }
      this.calcDailyRate()
    }
    this.monthlyRate = this.persistentMonthlyRate
    if (this.forceShowMonthlyRateInput) {
      this.showMonthlyInput()
    }
  },

  methods: {
    toggleRateType($e) {
      if ($e.target.checked) {
        this.showHourlyInput()
      } else {
        this.showDailyInput()
      }
    },
    showHourlyInput() {
      this.selectedRateType = 'hourly'
    },
    showDailyInput() {
      this.selectedRateType = 'daily'
    },
    showMonthlyInput() {
      this.monthlyRateEnabled = true
    },
    calcHourlyRate() {
      this.calcFrom('daily')
    },
    calcDailyRate() {
      this.calcFrom('hourly')
    },
    calcFrom(rateType) {
      if (rateType === 'hourly') {
        const dailyInteger = this.hourlyRateObj.integer * 8
        const dailyFraction = this.hourlyRateObj.fraction * 8
        const dailyRate = (dailyInteger + dailyFraction / 100).toFixed(2)
        this.dailyRateObj = {
          integer: dailyRate.slice(0, -3),
          fraction: dailyRate.slice(-2),
        }
      } else {
        const hourlyInteger = this.dailyRateObj.integer / 8
        const hourlyFraction = this.dailyRateObj.fraction / 8
        const hourlyRate = (hourlyInteger + hourlyFraction / 100).toFixed(2)
        this.hourlyRateObj = {
          integer: hourlyRate.slice(0, -3),
          fraction: hourlyRate.slice(-2),
        }
      }
    },
  },
}
</script>

<style>
.toggle-switch[data-ts-color='blue'] input + .ts-helper {
  background: rgba(50, 224, 185, 0.5) !important;
}
.toggle-switch[data-ts-color='blue'] input + .ts-helper:before {
  background: #30cca9;
}
</style>
